import React, { JSX } from "react";

export interface TabToggleOption<T extends string> {
  value: T;
  label: React.ReactNode;
}

/**
 * Borderless middot-style tab strip for switching a dialog's mode.
 *
 * The third of three segmented controls, and they are not interchangeable:
 * `SegmentedControl` is the compact inline variant used by column-format
 * pickers, `SegmentedTabs` is full-width and bordered, and this one is the quiet
 * unboxed style the search & replace redesign moved to deliberately — hierarchy
 * from type and space rather than boxes. Pick by the surrounding grammar.
 */
export function TabToggle<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: T;
  options: readonly TabToggleOption<T>[];
  onChange: (v: T) => void;
  ariaLabel: string;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex items-center item-height gap-2"
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            className={`tab-option cursor-pointer ${
              active ? "tab-option-active" : "tab-option-inactive"
            }`}
            style={{ fontSize: "var(--font-size)" }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
